<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        
        <div v-if="!isAdmin && !loading" class="no-data-container">
          <div class="empty-state">
            <div class="empty-icon"><i class="fas fa-user-lock" style="color: #e11d48;"></i></div>
            <h3>Không có quyền truy cập</h3>
            <p>Trang này chỉ dành cho cấp bậc Administrator.</p>
          </div>
        </div>

        <div v-else class="data-section">
          <div class="section-header">
            <h2><i class="fas fa-users-cog"></i> Quản Lý Tài Khoản Nhân Viên</h2>
            <div class="action-buttons">
              <button class="add-btn" @click="openCreateModal">
                <i class="fas fa-plus"></i> Thêm Nhân Viên
              </button>
            </div>
          </div>

          <!-- Loading indicator -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"><div class="spinner"><span></span></div></div>
            <p>Đang tải dữ liệu...</p>
          </div>

          <!-- Table for Desktop -->
          <div v-else-if="!loading && admins.length > 0" class="table-responsive desktop-only">
            <table class="admin-table">
              <thead>
                <tr>
                  <th>Họ Tên</th>
                  <th>Tên Đăng Nhập</th>
                  <th>Vai Trò</th>
                  <th>Trạng Thái</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="admin in admins" :key="admin.admin_id">
                  <td class="name">
                    <strong>{{ admin.full_name }}</strong>
                  </td>
                  <td>{{ admin.username }}</td>
                  <td>
                    <span class="status-badge" :class="getRoleClass(admin.role)">
                      <i :class="getRoleIcon(admin.role)"></i>
                      {{ getRoleLabel(admin.role) }}
                    </span>
                  </td>
                  <td class="status">
                    <span class="status-badge" :class="admin.is_active ? 'completed' : 'canceled'">
                      {{ admin.is_active ? 'Hoạt động' : 'Tạm khóa' }}
                    </span>
                  </td>
                  <td class="actions">
                    <button @click="openEditModal(admin)" class="action-btn edit-btn" title="Chỉnh sửa">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click="openResetPwModal(admin)" class="action-btn view-btn" title="Đặt lại mật khẩu">
                      <i class="fas fa-key"></i>
                    </button>
                    <button @click="toggleActive(admin)" class="action-btn" :class="admin.is_active ? 'cancel-btn' : 'success-btn'" :title="admin.is_active ? 'Vô hiệu hóa' : 'Mở khóa'">
                      <i :class="admin.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Cards for Mobile -->
          <div v-else-if="!loading && admins.length > 0" class="trip-cards mobile-only">
            <div class="trip-card" v-for="admin in admins" :key="'mob-' + admin.admin_id">
              <div class="trip-card-header">
                <div class="trip-name">{{ admin.full_name }}</div>
                <div class="status">
                  <span class="status-badge" :class="admin.is_active ? 'completed' : 'canceled'">
                    {{ admin.is_active ? 'Hoạt động' : 'Tạm khóa' }}
                  </span>
                </div>
              </div>
              <div class="trip-card-body">
                <div class="info-row">
                  <div class="info-label"><i class="fas fa-user"></i> Đăng nhập:</div>
                  <div class="info-value">{{ admin.username }}</div>
                </div>
                <div class="info-row">
                  <div class="info-label"><i class="fas fa-shield-alt"></i> Vai trò:</div>
                  <div class="info-value">
                    <span class="status-badge" :class="getRoleClass(admin.role)">
                      {{ getRoleLabel(admin.role) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="trip-card-footer">
                <button @click="openEditModal(admin)" class="card-action-btn edit-btn">
                  <i class="fas fa-edit"></i> Sửa
                </button>
                <button @click="openResetPwModal(admin)" class="card-action-btn view-btn">
                  <i class="fas fa-key"></i> Pass
                </button>
                <button @click="toggleActive(admin)" class="card-action-btn cancel-btn">
                  <i :class="admin.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                  {{ admin.is_active ? 'Khóa' : 'Mở' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loading" class="no-data-container">
            <div class="empty-state">
              <div class="empty-icon"><i class="fas fa-users-slash"></i></div>
              <h3>Không có dữ liệu nhân viên</h3>
              <button class="refresh-btn" @click="fetchAdmins">
                <i class="fas fa-sync-alt"></i> Tải lại dữ liệu
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Create Modal -->
    <div class="modal" v-if="showCreateModal" @click.self="showCreateModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2><i class="fas fa-user-plus"></i> Thêm Nhân Viên Mới</h2>
          <button class="close-btn" @click="showCreateModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="error-message" style="margin-bottom:1rem;">{{ error }}</div>
          <div class="form-group row">
            <div class="col-md-6">
              <label>Họ và Tên <span class="required">*</span></label>
              <input type="text" class="form-control" v-model="createForm.full_name" placeholder="Nguyễn Văn A" required />
            </div>
            <div class="col-md-6">
              <label>Tên đăng nhập <span class="required">*</span></label>
              <input type="text" class="form-control" v-model="createForm.username" placeholder="nguyenvana" required />
            </div>
          </div>
          <div class="form-group row">
            <div class="col-md-6">
              <label>Mật khẩu <span class="required">*</span></label>
              <input type="password" class="form-control" v-model="createForm.password" placeholder="Tối thiểu 6 ký tự" required />
            </div>
            <div class="col-md-6">
              <label>Cấp bậc</label>
              <select class="form-control" v-model="createForm.role">
                <option value="staff">Nhân Viên (Staff)</option>
                <option value="accountant">Kế Toán (Accountant)</option>
                <option value="admin">Quản Trị Viên (Admin)</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showCreateModal = false">Hủy</button>
          <button class="save-btn" @click="handleCreate" :disabled="saving">
            <i class="fas fa-spinner fa-spin" v-if="saving"></i> Lưu Dữ Liệu
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div class="modal" v-if="showEditModal" @click.self="showEditModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h2><i class="fas fa-user-edit"></i> Chỉnh Sửa Nhân Viên</h2>
          <button class="close-btn" @click="showEditModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="error" class="error-message" style="margin-bottom:1rem;">{{ error }}</div>
          <div class="form-group">
            <label>Họ và Tên</label>
            <input type="text" class="form-control" v-model="editForm.full_name" required />
          </div>
          <div class="form-group">
            <label>Cấp bậc</label>
            <select class="form-control" v-model="editForm.role">
              <option value="staff">Nhân Viên (Staff)</option>
              <option value="accountant">Kế Toán (Accountant)</option>
              <option value="admin">Quản Trị Viên (Admin)</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showEditModal = false">Hủy</button>
          <button class="save-btn" @click="handleEdit" :disabled="saving">
            <i class="fas fa-spinner fa-spin" v-if="saving"></i> Cập Nhật
          </button>
        </div>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div class="modal" v-if="showResetPwModal" @click.self="showResetPwModal = false">
      <div class="modal-content">
        <div class="modal-header delete-header">
          <h2><i class="fas fa-key"></i> Đặt Lại Mật Khẩu</h2>
          <button class="close-btn" @click="showResetPwModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="warning-message" style="margin-bottom:1rem; padding:10px; background:#fef3c7; border-radius:8px; color:#b45309">
            Thay đổi mật khẩu cho tài khoản <strong>{{ selectedAdmin?.username }}</strong>.
          </div>
          <div v-if="error" class="error-message" style="margin-bottom:1rem;">{{ error }}</div>
          <div class="form-group">
            <label>Mật khẩu mới <span class="required">*</span></label>
            <input type="password" class="form-control" v-model="resetPwForm.new_password" placeholder="Tối thiểu 6 ký tự" required />
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showResetPwModal = false">Hủy</button>
          <button class="save-btn" style="background:#d97706" @click="handleResetPassword" :disabled="saving">
            <i class="fas fa-spinner fa-spin" v-if="saving"></i> Xác nhận Đổi
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from '@/plugins/axios'
import AdminSidebar from '@/components/common/AdminSidebar.vue'

export default {
  name: 'AdminUsers',
  components: {
    AdminSidebar
  },
  data () {
    return {
      isSidebarOpen: window.innerWidth > 768,
      currentUser: {
        full_name: 'Admin',
        role: 'Administrator'
      },
      admins: [],
      loading: true,
      isAdmin: false,
      saving: false,
      error: null,
      showCreateModal: false,
      showEditModal: false,
      showResetPwModal: false,
      selectedAdmin: null,
      createForm: { username: '', password: '', full_name: '', role: 'staff' },
      editForm: { full_name: '', role: '' },
      resetPwForm: { new_password: '' }
    }
  },
  mounted () {
    this.checkRole()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
    },
    handleResize () {
      if (window.innerWidth <= 768) {
        this.isSidebarOpen = false
      } else {
        this.isSidebarOpen = true
      }
    },
    checkRole () {
      try {
        const token = localStorage.getItem('adminToken')
        if (!token) { this.loading = false; return }
        const payload = JSON.parse(atob(token.split('.')[1]))
        this.currentUser.full_name = payload.sub || 'Admin'
        this.isAdmin = payload.role === 'admin'
        if (this.isAdmin) {
          this.fetchAdmins()
        } else {
          this.loading = false
        }
      } catch (e) { 
        this.loading = false 
      }
    },
    async fetchAdmins () {
      try {
        this.loading = true
        const token = localStorage.getItem('adminToken')
        const { data } = await axios.get('/api/admin/users', { headers: { Authorization: `Bearer ${token}` } })
        this.admins = data
      } catch (err) {
        console.error('Error fetching admins:', err)
      } finally { this.loading = false }
    },
    openCreateModal () {
      this.error = null
      this.createForm = { username: '', password: '', full_name: '', role: 'staff' }
      this.showCreateModal = true
    },
    openEditModal (admin) {
      this.error = null
      this.selectedAdmin = admin
      this.editForm = { full_name: admin.full_name, role: admin.role }
      this.showEditModal = true
    },
    openResetPwModal (admin) {
      this.error = null
      this.selectedAdmin = admin
      this.resetPwForm = { new_password: '' }
      this.showResetPwModal = true
    },
    async handleCreate () {
      this.error = null
      if (!this.createForm.username || !this.createForm.password || !this.createForm.full_name) {
        this.error = 'Vui lòng điền đầy đủ thông tin'; return
      }
      this.saving = true
      try {
        const token = localStorage.getItem('adminToken')
        await axios.post('/api/admin/users', this.createForm, { headers: { Authorization: `Bearer ${token}` } })
        this.showCreateModal = false
        this.fetchAdmins()
      } catch (err) { this.error = err.response?.data?.detail || 'Lỗi hệ thống' }
      finally { this.saving = false }
    },
    async handleEdit () {
      if (!this.selectedAdmin) return
      this.error = null
      this.saving = true
      try {
        const token = localStorage.getItem('adminToken')
        await axios.put(`/api/admin/users/${this.selectedAdmin.admin_id}`, this.editForm, { headers: { Authorization: `Bearer ${token}` } })
        this.showEditModal = false
        this.fetchAdmins()
      } catch (err) { this.error = err.response?.data?.detail || 'Lỗi hệ thống' }
      finally { this.saving = false }
    },
    async handleResetPassword () {
      if (!this.selectedAdmin) return
      this.error = null
      this.saving = true
      try {
        const token = localStorage.getItem('adminToken')
        await axios.post(`/api/admin/users/${this.selectedAdmin.admin_id}/reset-password`, this.resetPwForm, { headers: { Authorization: `Bearer ${token}` } })
        this.showResetPwModal = false
      } catch (err) { this.error = err.response?.data?.detail || 'Lỗi hệ thống' }
      finally { this.saving = false }
    },
    async toggleActive (admin) {
      const action = admin.is_active ? 'deactivate' : 'activate'
      if (!confirm(`Xác nhận thao tác cho tài khoản "${admin.username}"?`)) return
      try {
        const token = localStorage.getItem('adminToken')
        await axios.post(`/api/admin/users/${admin.admin_id}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } })
        this.fetchAdmins()
      } catch (err) { alert('Lỗi trạng thái') }
    },
    getRoleLabel (role) {
      const map = { admin: 'Admin', staff: 'Staff', accountant: 'Kế Toán' }
      return map[role] || role
    },
    getRoleClass (role) {
      if (role === 'admin') return 'completed'
      if (role === 'accountant') return 'exchange'
      return 'scheduled'
    },
    getRoleIcon (role) {
      if (role === 'admin') return 'fas fa-crown'
      if (role === 'accountant') return 'fas fa-calculator'
      return 'fas fa-user'
    }
  }
}
</script>

<style scoped>
/* Only add micro-overrides here. The rest handles globally. */
.form-group.row {
  display: flex;
  gap: 15px;
}
.col-md-6 {
  flex: 1;
}
.success-btn {
  background-color: #f0fdf4;
  color: #166534;
  border-color: #bbf7d0;
}
.success-btn:hover {
  background-color: #dcfce7;
  color: #15803d;
}
@media (max-width: 768px) {
  .form-group.row {
    flex-direction: column;
    gap: 0;
  }
}
</style>
